"use client";

import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ChevronDownIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCamundaOptions } from '@/hooks/use-camunda-options';

interface DropdownMenuFieldOption {
  value: string;
  label: string;
}

interface DropdownMenuFieldProps {
  name: string;
  label: string;
  placeholder?: string;
  options?: DropdownMenuFieldOption[];
  loadOptions?: boolean;
  required?: boolean;
  disabled?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  onValueChange?: (value: string) => void;
}

export function DropdownMenuField({ 
  name, 
  label, 
  placeholder = "Selecciona una opción",
  options: providedOptions,
  loadOptions = false,
  required = false,
  disabled = false,
  allowEmpty = false,
  emptyLabel = 'Sin seleccion',
  onValueChange,
}: DropdownMenuFieldProps) {
  const { control, setValue } = useFormContext();
  const watchedValue = useWatch({ control, name });
  const watchedLabel = useWatch({ control, name: `${name}_label` });
  
  const camundaOptions = useCamundaOptions(name);
  const baseOptions = loadOptions ? camundaOptions : (providedOptions || []);
  const watchedValueStr = watchedValue != null && watchedValue !== '' ? String(watchedValue) : '';
  const watchedLabelStr = watchedLabel != null ? String(watchedLabel).trim() : '';
  const syntheticOption =
    watchedValueStr && !baseOptions.some((option) => option.value === watchedValueStr)
      ? { value: watchedValueStr, label: watchedLabelStr || watchedValueStr }
      : null;
  const options = syntheticOption ? [syntheticOption, ...baseOptions] : baseOptions;
  
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedOption = options.find(option => option.value === String(field.value ?? ''));

        const handleSelect = (option: DropdownMenuFieldOption) => {
          if (disabled) {
            return;
          }
          field.onChange(option.value);
          setValue(`${name}_label`, option.label);
          onValueChange?.(option.value);
        };

        const handleClear = () => {
          if (disabled) {
            return;
          }
          field.onChange('');
          setValue(`${name}_label`, '');
          onValueChange?.('');
        };
        
        return (
          <FormItem className="flex flex-col">
            <FormLabel>{label} {required && '*'}</FormLabel>
            <FormControl>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    aria-haspopup="menu"
                    disabled={disabled}
                    className={cn(
                      "w-full justify-between",
                      !selectedOption && "text-muted-foreground",
                      "disabled:bg-muted"
                    )}
                  >
                    {selectedOption ? selectedOption.label : placeholder}
                    <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                  {allowEmpty ? (
                    <DropdownMenuItem
                      onClick={handleClear}
                      className="cursor-pointer text-muted-foreground"
                    >
                      {emptyLabel}
                    </DropdownMenuItem>
                  ) : null}
                  {options.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => handleSelect(option)}
                      className="cursor-pointer"
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
